import React from 'react'

import {
  AlignItems,
  Box,
  Display,
  FlexDirection,
  Heading1,
  Heading2,
  JustifyContent,
  Body1,
  Body2,
  Space,
  Text,
  TextAlign,
  Link,
  Col,
  Grid,
  BorderStyle,
  Color,
} from '@gousto-internal/citrus-react'
import Helmet from 'react-helmet'

import { getStaticAssetURL } from '@library/static-assets'

import { privacyPolicyConfig } from 'config/privacyPolicy'
import { client } from 'config/routes'

function TableRow({ children, last }: { children: React.ReactNode; last?: boolean }) {
  return (
    <Grid
      role="row"
      borderColor={Color.ColdGrey_800}
      borderBottomWidth={last ? 0.5 : 0}
      borderWidth={0.5}
      borderStyle={BorderStyle.Solid}
    >
      {children}
    </Grid>
  )
}

function ColumnHeader({ children, size }: { children: string; size: 4 | 8 }) {
  return (
    <Col
      alignItems={AlignItems.Center}
      borderColor={Color.ColdGrey_800}
      borderRightWidth={0.5}
      borderStyle={BorderStyle.Solid}
      display={Display.Flex}
      bg={Color.Secondary_300}
      paddingH={2}
      paddingV={2}
      role="columnheader"
      size={size}
    >
      <Body2 color={Color.White}>{children}</Body2>
    </Col>
  )
}

function TableCell({
  children,
  size,
  borderRight,
}: {
  children: React.ReactNode
  size: 4 | 8
  borderRight?: boolean
}) {
  return (
    <Col
      alignItems={AlignItems.FlexStart}
      display={Display.Flex}
      paddingH={2}
      paddingV={2}
      role="cell"
      size={size}
      borderColor={Color.ColdGrey_800}
      borderRightWidth={borderRight ? 0.5 : 0}
      borderStyle={BorderStyle.Solid}
    >
      <Body2 style={{ wordBreak: 'break-word' }}>{children}</Body2>
    </Col>
  )
}

const privacyPolicyTableData = [
  {
    purpose: 'Provide services',
    whatWeUse: (
      <>
        We use your contact and transaction information to provide our services to you. This
        includes things like:
        <ul>
          <li>setting up and managing your account</li>
          <li>communicating with you about your account and orders</li>
          <li>completing your deliveries</li>
          <li>dealing with queries and complaints</li>
        </ul>
      </>
    ),
    lawfulBasis: (
      <>
        <strong>Performance of contract</strong> to fulfil the terms of our agreements with our
        customers.
        <br />
        <br />
        <strong>Legitimate interests</strong> to monitor and improve our services.
      </>
    ),
  },
  {
    purpose: 'Process payments',
    whatWeUse: 'We use your data to process your payments, orders and refunds.',
    lawfulBasis: (
      <>
        <strong>Performance of contract</strong> to fulfil the terms of our agreements with our
        customers.
      </>
    ),
  },
  {
    purpose: 'Develop and improve our products and services',
    whatWeUse: (
      <>
        We use your contact information and feedback to:
        <ul>
          <li>collect feedback about our recipes</li>
          <li>give our recipes a star rating</li>
          <li>enable you to participate in a Gousto prize draw or competition</li>
          <li>collect Gousto survey responses</li>
        </ul>
        This form of processing allows us to reward our customers and to develop and improve our
        products and services.
      </>
    ),
    lawfulBasis: (
      <>
        <strong>Legitimate interests</strong> to understand our customers and improve our products
        and services.
        <br />
        <br />
        <strong>Consent</strong> where relevant to use your data in this way.
      </>
    ),
  },
  {
    purpose: 'Reward our customers',
    whatWeUse:
      'We use your contact information to enable you to participate in a Gousto prize draw or competition.',
    lawfulBasis: (
      <>
        <strong>Consent</strong> to use your data in this way.
      </>
    ),
  },
  {
    purpose: 'Maintaining our platform and app',
    whatWeUse: (
      <>
        We use your behavioural data to manage our website and app as well as help keep these
        platforms secure. To do this, we may use strictly necessary cookies which you can find out
        more about by reading our <Link href={client.privacyCookiesPolicies}>Cookies Policy</Link>.
      </>
    ),
    lawfulBasis: (
      <>
        <strong>Performance of contract</strong> to fulfil the terms of our agreements with our
        customers.
      </>
    ),
  },
  {
    purpose: 'Improve our online customer experience',
    whatWeUse:
      'We also use your behavioural data to experiment, test, troubleshoot and conduct data analysis to provide you with the best possible customer experience.',
    lawfulBasis: (
      <>
        <strong>Legitimate interests</strong> to ensure our platforms are operating as expected, to
        efficiently present our content to you and to keep our platforms safe.
        <br />
        <br />
        <strong>Consent</strong> to use analytics cookies, which you can find out more about by
        reading our <Link href={client.privacyCookiesPolicies}>Cookies Policy</Link>.
      </>
    ),
  },
  {
    purpose: 'Marketing, gifting and samples',
    whatWeUse: (
      <>
        We use your contact information to:
        <ul>
          <li>
            send you marketing emails, mail, calls or SMS as long as we&apos;ve received your
            permission. We get in touch with details about our goods and services. These might
            include special offers, discounts, events, competitions or promotions
          </li>
          <li>
            send you marketing emails, mail, calls or SMS from trusted third parties as long as
            we&apos;ve received your permission to send you gifts, free samples and other
            promotional material with your Gousto box that we think may be of interest to you
          </li>
        </ul>
        We also use your website, app, product and order history data to:
        <ul>
          <li>
            personalise your experience when you use our services so that they&apos;re more relevant
            and interesting to you
          </li>
          <li>measure and improve our advertising</li>
          <li>recommend recipes</li>
        </ul>
        We also use anonymised account data to build marketing personas. To do this we may combine
        your personal data with the data from lots of other customers, as well as any data we
        receive from third parties. We always anonymise your data to make sure your information is
        secure. This means we change your personal data so that it&apos;s no longer classed as
        personal data and you can&apos;t be identified by it.
      </>
    ),
    lawfulBasis: (
      <>
        <strong>Consent</strong> to use your data in this way.
        <br />
        <br />
        <strong>Consent</strong> to use marketing cookies, which you can find out more about by
        reading our <Link href={client.privacyCookiesPolicies}>Cookies Policy</Link>.<br />
        <br />
        <strong>Legitimate interests</strong> to support our marketing activities and be efficient
        about how to conduct our marketing activities.
      </>
    ),
  },
  {
    purpose: 'Meet our legal obligations and prevent fraud',
    whatWeUse: (
      <>
        We use your contact information, transaction history, payment information and behavioural
        information to:
        <ul>
          <li>comply with laws and regulations that we&apos;re subject to</li>
          <li>comply with requests made by law enforcement where required</li>
        </ul>
      </>
    ),
    lawfulBasis: (
      <>
        <strong>Legal obligation</strong>
      </>
    ),
  },
  {
    purpose: 'Keep your account secure and prevent fraud',
    whatWeUse: (
      <>
        We use your contact information, transaction history, payment information and behavioural
        information to:
        <ul>
          <li>detect and prevent fraudulent activity</li>
          <li>keep our services secure</li>
          <li>keep your account secure</li>
        </ul>
      </>
    ),
    lawfulBasis: (
      <>
        <strong>Legal obligation</strong>
      </>
    ),
  },
  {
    purpose: 'Provide location-based services',
    whatWeUse: (
      <>
        We may use your IP address and other customer behaviour data to:
        <ul>
          <li>recognise you when you visit or return to our website or apps</li>
          <li>
            to track anonymised traffic and usage patterns. We always anonymise your data to make
            sure your information is secure. This means we change your personal data so that you can
            no longer be identified by it and so that it&apos;s no longer classed as personal data.
          </li>
        </ul>
      </>
    ),
    lawfulBasis: (
      <>
        <strong>Legitimate interests</strong> to provide our customers with the best possible
        experience.
        <br />
        <br />
        <strong>Performance of contract</strong> to fulfil the terms of our agreements with our
        customers. We may use cookies to do this, which you can find out more about by reading our{' '}
        <Link href={client.privacyCookiesPolicies}>Cookies Policy</Link>.
      </>
    ),
  },
]

function PrivacyPolicyTable() {
  return (
    <Box role="table">
      <TableRow>
        <ColumnHeader size={4}>Purpose</ColumnHeader>
        <ColumnHeader size={4}>What we use and how we use it</ColumnHeader>
        <ColumnHeader size={4}>Lawful basis</ColumnHeader>
      </TableRow>
      {privacyPolicyTableData.map(({ purpose, whatWeUse, lawfulBasis }, index) => {
        const last = index === privacyPolicyTableData.length - 1

        return (
          <TableRow key={purpose} last={last}>
            <TableCell size={4} borderRight>
              {purpose}
            </TableCell>
            <TableCell size={4} borderRight>
              {whatWeUse}
            </TableCell>
            <TableCell size={4}>{lawfulBasis}</TableCell>
          </TableRow>
        )
      })}
    </Box>
  )
}

export const PrivacyPolicy: React.FC = () => (
  <>
    <Helmet title={privacyPolicyConfig.head.title} meta={privacyPolicyConfig.head.meta} />
    <Box
      paddingH={[3, 8]}
      paddingV={[10, 14]}
      display={Display.Flex}
      justifyContent={JustifyContent.Center}
      alignItems={AlignItems.Center}
    >
      <Box
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        flexDirection={FlexDirection.Column}
        width="100%"
        maxWidth="1200px"
        gap={[6, 8]}
      >
        <Text size={2}>Version {privacyPolicyConfig.version}</Text>
        <Text size={2}>Last updated: {privacyPolicyConfig.lastUpdated}</Text>
        <Body1>
          <Link href={`${getStaticAssetURL('/pdfs/Gousto_Privacy_Policy_V3.pdf')}`} target="_blank">
            View this as a PDF
          </Link>
        </Body1>
        <Heading1 textAlign={TextAlign.Center}>Gousto Privacy Policy</Heading1>
        <Space direction="vertical" size={4} />

        <section id="your-privacy-our-priority">
          <Heading2>Your privacy, our priority</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            Here at Gousto, your data&apos;s in safe hands. Not only do we promise to collect only
            the essentials we need to make dinnertime as delicious as can be, but we&apos;ll always
            keep you in the loop about how, when and why we use your data.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="privacy-123">
          <Heading2>Privacy 1, 2 and 3</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            When it comes to your personal data, these are the three most important things you need
            to know.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>1.</strong> We use as little data as possible to process your order, deliver
            your dinners, improve your experience online and contact you about things that might
            interest you.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>2.</strong> We keep your data safe and secure, and will never keep it longer
            than necessary.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>3.</strong> We&apos;ll always keep you in the loop when it comes to your data.
            And remember, you and your information are protected by data protection law.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="is-this-policy-for-me">
          <Heading2>Is this policy for me?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            The Gousto Privacy Policy lets you know how we deal with your personal data. That&apos;s
            the information we know about you or that can be used to identify you. Plus, we&apos;ll
            describe in detail all the kinds of data we collect, how it&apos;s used and protected,
            who we share it with, how long we keep it and how you can access and correct it.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="whos-responsible">
          <Heading2>1. Who&apos;s responsible for my data?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            That&apos;s us. We&apos;re SCA Investments Limited trading as &quot;Gousto&quot;. We own
            and operate our website <Link href="https://www.gousto.co.uk">www.gousto.co.uk</Link>{' '}
            and the Gousto app, as well as any of the services available to you in-app or online.
            We&apos;re also the Data Controller responsible for protecting your personal data and
            privacy.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="why-give-data">
          <Heading2>2. Why should I give Gousto my data?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            We need your data to be able to deliver your delicious dinners. For example, we&apos;ll
            need your contact details when you open your Gousto account to deliver your meals and
            provide updates on your orders. Providing us with your personal data allows us to enter
            into a contract with you and perform our obligations under the contract. If you&apos;re
            unwilling or unable to share your personal data with us, we may be unable to enter into
            and/or perform the contract with you. This may lead to us cancelling or suspending a
            product or service you have with us. However, if we were to do this we&apos;ll notify
            you in advance.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="what-law-says">
          <Heading2>3. What does the law say about the data Gousto needs?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            Under General Data Protection Regulation (GDPR) we need valid legal reasons for using
            your data. This is called lawful basis. Our legal bases will usually be one of the
            following:
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>3.1 Performance of contract.</strong> We need to use data like your contact and
            payment information to sell you our products and complete your orders.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>3.2 Consent.</strong> As part of the Gousto experience, you may agree to us
            collecting and using your data. This may be in the form of opting into emails you
            receive from us.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>3.3 Legitimate interests.</strong> We sometimes use your data because we have a
            legitimate reason to do so. This includes things like providing you with the best
            product and service or keeping your data if we need to recover debt. We&apos;ll always
            balance your rights and freedoms against our legitimate reasons to use or retain your
            personal data.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>3.4 Legal compliance.</strong> There may be cases when we&apos;re required to
            use your personal data to comply with legal or regulatory obligations that we&apos;re
            subject to.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="how-gousto-uses-data">
          <Heading2>4. How exactly does Gousto use my data?</Heading2>
          <Space direction="vertical" size={4} />
          <PrivacyPolicyTable />
        </section>

        <Space direction="vertical" size={6} />

        <section id="how-long-keep-data">
          <Heading2>5. How long will you keep my data?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            <strong>5.1</strong> We keep different types of information for different periods of
            time. We&apos;ll only keep your personal information for as long as is reasonably
            necessary for the purposes we originally collected it. We&apos;ll always delete or
            anonymise personal data when it&apos;s no longer needed unless we&apos;re legally
            obliged to hold onto it. When choosing how long we hold on to your personal data, we
            consider the following:
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            • the amount, nature, and sensitivity of the personal data;
            <br />
            • the potential risk of harm from unauthorised use or disclosure of your personal data;
            <br />
            • the purpose for which we process your personal data;
            <br />
            • whether we can achieve those purposes through other means;
            <br />• applicable legal requirements;
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>5.2</strong> We may keep hold of your data to contact you with product updates
            or offers at a later date. We keep this data for up to 36 months after you have stopped
            using Gousto. This means we may email you or send an SMS if you haven&apos;t ordered for
            a while. Remember, you can opt out of all marketing emails at any time in your account.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="how-protect-data">
          <Heading2>6. How will you protect my data?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            <strong>6.1</strong> Security is a priority for us. We&apos;re continuously implementing
            and updating administrative, technical and physical security measures to help protect
            your personal data. We protect your data and treat it with the respect it deserves.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>6.2</strong> Your data may be transferred, stored and/ or processed outside the
            UK and/or the EEA as our suppliers sometimes operate from outside of the UK and/or EEA.
            We&apos;ll only transfer your data outside of the UK and/or EEA in compliance with data
            protection laws and provided appropriate or suitable safeguards are in place to protect
            your data, such as Standard Contractual Clauses, Binding Corporate Rules (including the
            Mastercard Binding Corporate Rules) or any other clauses, mechanisms or safeguards to
            ensure compliance.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="who-share-data">
          <Heading2>7. Who do you share my data with?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            <strong>7.1 Service providers.</strong> We may allow third parties who supply us with a
            service access to your personal data. Examples include (i) e-commerce platform
            providers, (ii) couriers such as{' '}
            <Link href="https://www.yodel.co.uk/privacy-cookies-policy" target="_blank">
              Yodel
            </Link>{' '}
            or{' '}
            <Link href="https://www.collectplus.co.uk/privacy/" target="_blank">
              CollectPlus
            </Link>
            , (iii) IT service providers that host, manage and service our data, (iv) website hosts,
            (v) content delivery networks and (vi) businesses that assist us in undertaking
            communications, monitoring, testing or improving our website or apps.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>7.2 Banks and payment partners.</strong> We may share your payment information
            and email address with banks and payment service providers such as PayPal, SagePay,
            checkout.com and First Data (our payment gateway and providers) to process the payments
            of your orders and any potential refund, as well as credit reference and fraud
            prevention agencies, who may keep a record of that information.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>7.3 Trustpilot.</strong> We may disclose your contact details to Trustpilot to
            enable it to email you inviting you to leave a review on our site.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>7.4 Law enforcement agencies.</strong> We may disclose personal data so far as
            reasonably necessary if we have reason to believe that it breaches our terms and
            conditions, or that such steps are necessary to protect us or others, or that a criminal
            act has been committed, or if there has been a complaint about content posted by you, or
            if we&apos;re required to do so by law or appropriate authority.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>7.5 Professional advisors.</strong> We may at times need to share your personal
            data with lawyers, bankers, auditors and insurers who provide us with consultancy,
            legal, banking, accounting and insurance services. Where appropriate, we will always
            seek to anonymise your data before we share it with these organisations.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>7.7 Gifting partners.</strong> We may also share your contact details with third
            parties to send you gifts or loyalty rewards on behalf of Gousto.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="data-protection-rights">
          <Heading2>8. What are my data protection rights?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            You&apos;re covered by data protection laws that give you the right to request a copy,
            correct or the deletion of your information. You&apos;ve also got the right to opt out
            of communications. Read on for your full list of rights.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>8.1 Update or correct your information:</strong> you can easily correct or
            change your personal/ contact information on your &quot;My Details&quot; page by logging
            in to your account on our website or apps.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>8.2 Access your information:</strong> you&apos;ve got the right to request
            personal data that we hold about you, subject to us reserving the right to withhold such
            data to the extent permitted by law.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>8.3 Delete your information:</strong> you&apos;ve got the right to request that
            we delete all the personal data that we hold about you. Remember, we may be legally
            entitled to keep hold of some of your personal data.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>8.4 Opt out of marketing communications:</strong> you can easily change your
            marketing permissions on your &quot;Account Details&quot; page, under your
            &quot;Communication Preferences&quot; section by logging in to your account on our
            website or apps. For more information on how to do this please see{' '}
            <Link
              href="https://www.gousto.co.uk/help-centre/article/how-do-i-update-my-communication-preferences-/4421786292497"
              target="_blank"
            >
              here
            </Link>
            .
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>8.5 Revoke your consent:</strong> if you&apos;ve given us your consent to use
            your personal data, you can withdraw us from processing it at any time. Please note that
            it will have been lawful for us to use your personal data up to the point you withdraw
            your permission.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>8.6 Object to us processing your information:</strong> if we&apos;re relying on
            &quot;legitimate interests&quot; as the legal basis of our processing of your personal
            data and you disagree with us using it, then you can object. However, if there is a
            compelling reason why we need to use your personal data, we may be able to continue to
            do so. Otherwise, we will stop processing it.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>8.7 Have the processing of your information restricted:</strong> in some
            circumstances, you can ask us to restrict the processing of your personal data. For
            example, you might want us to pause processing payments until we&apos;ve corrected your
            bank details or want to close your account and want us to keep a record of your
            information.
          </Body1>
          <Space direction="vertical" size={2} />
          <Body1>
            <strong>8.8 Have your information transferred to a third party:</strong> in some
            circumstances, we can provide your personal data to a third party in a secured, commonly
            used and machinereadable format on your request.
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="policy-changes">
          <Heading2>9. Will this policy change?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            We update our privacy policy now and then but don&apos;t worry, if we make any material
            changes, we&apos;ll let you know by email. New versions will be posted on our website.
            We&apos;ll assume you agree to the revised privacy policy if you use our website or apps
            after the effective date shown at the bottom of this privacy policy.
          </Body1>
          <Space direction="vertical" size={3} />
          <Body1>
            <strong>Effective date:</strong> Our privacy policy was last updated on{' '}
            {privacyPolicyConfig.lastUpdated}
            (Version {privacyPolicyConfig.version})
          </Body1>
        </section>

        <Space direction="vertical" size={6} />

        <section id="get-in-touch">
          <Heading2>How can I get in touch?</Heading2>
          <Space direction="vertical" size={4} />
          <Body1>
            The quickest and easiest way to talk to us about your personal data is through our
            friendly{' '}
            <Link href="https://gousto.zendesk.com/hc/en-gb/requests/new">Customer Care team</Link>.
            You can also contact our Data Protection Officer at{' '}
            <Link href="mailto:dpo@gousto.co.uk">dpo@gousto.co.uk</Link>. While we&apos;d love to be
            your first port of call, you have the right to contact the{' '}
            <Link href="https://ico.org.uk/concerns/">
              Information Commissioner&apos;s Office (ICO)
            </Link>{' '}
            directly. We&apos;re registered with the Information Commissioner&apos;s Register of
            Data Controllers under number ZA029698.
          </Body1>
        </section>

        <Space direction="vertical" size={4} />
      </Box>
    </Box>
  </>
)
